import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  ClipboardList, 
  FileText, 
  Loader2, 
  ChevronRight,
  Sparkles,
  ExternalLink,
  IdCard,
  Car,
  Heart,
  Briefcase,
  Home,
  Users,
  Landmark,
  Receipt,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { 
  DEMARCHES_CATALOG, 
  DemarcheTemplate,
  getDemarchesByCategory,
  DemarcheCategory,
} from "@/config/demarchesConfig";

interface CreateDemarcheFromActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document: {
    id: string;
    title: string;
    doc_type: string | null;
  };
  onSuccess: () => void;
}

// Map doc_type to suggested templates
const DOC_TYPE_TO_TEMPLATES: Record<string, string[]> = {
  'identity': ['cni_adulte_renouvellement', 'cni_mineur_premiere'],
  'passport': ['passeport_adulte_renouvellement', 'passeport_mineur_premiere'],
  'driving_license': ['permis_perte_vol'],
  'vehicle': ['carte_grise_changement_adresse', 'immatriculation_occasion', 'controle_technique'],
  'insurance': [], // Free form for now
  'medical': [],
  'tax': ['declaration_revenus'],
  'invoice': [],
  'payslip': [],
  'contract': [],
  'bank': ['ouverture_compte'],
};

// Map doc_type to category for filtering
const DOC_TYPE_TO_CATEGORY: Record<string, DemarcheCategory> = {
  'identity': 'identite',
  'passport': 'identite',
  'driving_license': 'vehicule',
  'vehicle': 'vehicule',
  'insurance': 'banque',
  'medical': 'sante',
  'tax': 'fiscal',
  'invoice': 'logement',
  'payslip': 'pro',
  'contract': 'pro',
  'bank': 'banque',
};

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  'identite': IdCard,
  'vehicule': Car,
  'sante': Heart,
  'pro': Briefcase,
  'logement': Home,
  'famille': Users,
  'banque': Landmark,
  'fiscal': Receipt,
};

type Mode = 'choose' | 'template' | 'free';

export function CreateDemarcheFromActionDialog({
  open,
  onOpenChange,
  document,
  onSuccess,
}: CreateDemarcheFromActionDialogProps) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('choose');
  const [selectedTemplate, setSelectedTemplate] = useState<DemarcheTemplate | null>(null);
  const [freeTitle, setFreeTitle] = useState('');
  const [freeDescription, setFreeDescription] = useState('');
  const [creating, setCreating] = useState(false);

  // Get suggested templates based on doc_type
  const suggestedTemplates = useMemo(() => {
    if (!document.doc_type) return [];
    
    const templateKeys = DOC_TYPE_TO_TEMPLATES[document.doc_type] || [];
    return DEMARCHES_CATALOG.filter(t => templateKeys.includes(t.key));
  }, [document.doc_type]);

  // Get related templates from same category
  const relatedTemplates = useMemo(() => {
    if (!document.doc_type) return DEMARCHES_CATALOG.slice(0, 6);
    
    const category = DOC_TYPE_TO_CATEGORY[document.doc_type];
    if (!category) return DEMARCHES_CATALOG.slice(0, 6);
    
    return getDemarchesByCategory(category).filter(
      t => !suggestedTemplates.some(st => st.key === t.key)
    ).slice(0, 4);
  }, [document.doc_type, suggestedTemplates]);

  const resetState = () => {
    setMode('choose');
    setSelectedTemplate(null);
    setFreeTitle('');
    setFreeDescription('');
    setCreating(false);
  };

  const handleClose = () => {
    resetState();
    onOpenChange(false);
  };

  const handleSelectTemplate = (template: DemarcheTemplate) => {
    setSelectedTemplate(template);
    setMode('template');
  };

  const handleCreateFromTemplate = async () => {
    if (!selectedTemplate) return;
    
    setCreating(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      // Create démarche
      const { data: demarche, error: demarcheError } = await supabase
        .from('demarches')
        .insert({
          user_id: user.id,
          title: selectedTemplate.label,
          description: selectedTemplate.description,
          template_key: selectedTemplate.key,
          status: 'pending',
          source_document_id: document.id,
          official_url: selectedTemplate.official_url,
          due_date: selectedTemplate.estimated_days 
            ? new Date(Date.now() + selectedTemplate.estimated_days * 24 * 60 * 60 * 1000).toISOString()
            : null,
        })
        .select()
        .single();

      if (demarcheError) throw demarcheError;

      // Create document requirements
      if (selectedTemplate.documents.length > 0) {
        const docInserts = selectedTemplate.documents.map(doc => ({
          demarche_id: demarche.id,
          document_key: doc.key,
          label: doc.label,
          doc_type: doc.doc_type,
          is_required: doc.is_required,
          validity_rule: doc.validity_rule,
          description: doc.description,
        }));

        await supabase.from('demarche_documents').insert(docInserts);
      }

      // Create steps
      if (selectedTemplate.steps.length > 0) {
        const stepInserts = selectedTemplate.steps.map((step, index) => ({
          demarche_id: demarche.id,
          step_key: step.key,
          label: step.label,
          step_type: step.step_type,
          url: step.url,
          description: step.description,
          order_index: index,
        }));

        await supabase.from('demarche_steps').insert(stepInserts);
      }

      // NOTE: We no longer dismiss the expiry alert here
      // The document stays visible in Actions with a "Démarche en cours" badge
      // until the démarche is completed

      toast.success("Démarche créée avec succès !");
      onSuccess();
      handleClose();
      
      // Navigate to the new démarche
      navigate(`/demarches/${demarche.id}`);
    } catch (error) {
      console.error('Error creating démarche:', error);
      toast.error("Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  const handleCreateFree = async () => {
    if (!freeTitle.trim()) {
      toast.error("Veuillez entrer un titre");
      return;
    }
    
    setCreating(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { data: demarche, error } = await supabase
        .from('demarches')
        .insert({
          user_id: user.id,
          title: freeTitle.trim(),
          description: freeDescription.trim() || null,
          status: 'pending',
          source_document_id: document.id,
        })
        .select()
        .single();

      if (error) throw error;

      // NOTE: We no longer dismiss the expiry alert here
      // The document stays visible in Actions with a "Démarche en cours" badge

      toast.success("Démarche créée avec succès !");
      onSuccess();
      handleClose();
      
      navigate(`/demarches/${demarche.id}`);
    } catch (error) {
      console.error('Error creating démarche:', error);
      toast.error("Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  const CategoryIcon = document.doc_type 
    ? CATEGORY_ICONS[DOC_TYPE_TO_CATEGORY[document.doc_type] || 'pro'] 
    : ClipboardList;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-primary" />
            Créer une démarche
          </DialogTitle>
          <DialogDescription>
            {mode === 'choose' && "Choisissez un modèle ou créez une démarche personnalisée"}
            {mode === 'template' && selectedTemplate?.label}
            {mode === 'free' && "Démarche personnalisée"}
          </DialogDescription>
        </DialogHeader>

        {/* Document context */}
        <div className="p-3 bg-muted/50 rounded-lg flex items-center gap-3 mb-2">
          <FileText className="w-5 h-5 text-muted-foreground shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{document.title}</p>
            <p className="text-xs text-muted-foreground">Document lié</p>
          </div>
        </div>

        {mode === 'choose' && (
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-4 pr-3">
              {/* Suggested templates */}
              {suggestedTemplates.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">Suggestions</span>
                  </div>
                  <div className="space-y-2">
                    {suggestedTemplates.map(template => (
                      <button
                        key={template.key}
                        onClick={() => handleSelectTemplate(template)}
                        className="w-full p-3 text-left rounded-lg border hover:bg-muted/50 transition-colors group"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                              <CategoryIcon className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{template.label}</p>
                              <p className="text-xs text-muted-foreground line-clamp-1">
                                {template.documents.length} documents • {template.steps.length} étapes
                              </p>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Related templates */}
              {relatedTemplates.length > 0 && (
                <div className="space-y-2">
                  <span className="text-sm font-medium text-muted-foreground">Autres démarches</span>
                  <div className="space-y-2">
                    {relatedTemplates.map(template => (
                      <button
                        key={template.key}
                        onClick={() => handleSelectTemplate(template)}
                        className="w-full p-3 text-left rounded-lg border hover:bg-muted/50 transition-colors group"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{template.label}</p>
                            <p className="text-xs text-muted-foreground">
                              {template.documents.length} documents • {template.steps.length} étapes
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Free form option */}
              <div className="pt-2 border-t">
                <button
                  onClick={() => setMode('free')}
                  className="w-full p-3 text-left rounded-lg border border-dashed hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                      <ClipboardList className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Démarche personnalisée</p>
                      <p className="text-xs text-muted-foreground">
                        Créez votre propre démarche sans modèle
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </ScrollArea>
        )}

        {mode === 'template' && selectedTemplate && (
          <div className="space-y-4">
            <div className="p-4 rounded-lg border bg-card">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <CategoryIcon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">{selectedTemplate.label}</h3>
                  <p className="text-sm text-muted-foreground">{selectedTemplate.description}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1.5">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <span>{selectedTemplate.documents.length} documents</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <ClipboardList className="w-4 h-4 text-muted-foreground" />
                  <span>{selectedTemplate.steps.length} étapes</span>
                </div>
                {selectedTemplate.estimated_days && (
                  <Badge variant="outline">
                    ~{selectedTemplate.estimated_days}j
                  </Badge>
                )}
              </div>

              {selectedTemplate.official_url && (
                <a 
                  href={selectedTemplate.official_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 text-xs text-primary hover:underline inline-flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" />
                  Source officielle
                </a>
              )}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setMode('choose')}
            >
              ← Choisir un autre modèle
            </Button>
          </div>
        )}

        {mode === 'free' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Titre de la démarche *</Label>
              <Input
                id="title"
                value={freeTitle}
                onChange={(e) => setFreeTitle(e.target.value)}
                placeholder="Ex: Renouvellement de mon Kbis"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description (optionnel)</Label>
              <Input
                id="description"
                value={freeDescription}
                onChange={(e) => setFreeDescription(e.target.value)}
                placeholder="Détails supplémentaires..."
              />
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setMode('choose')}
            >
              ← Retour
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={creating}>
            Annuler
          </Button>
          
          {mode === 'template' && (
            <Button onClick={handleCreateFromTemplate} disabled={creating}>
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Création...
                </>
              ) : (
                "Créer la démarche"
              )}
            </Button>
          )}
          
          {mode === 'free' && (
            <Button onClick={handleCreateFree} disabled={creating || !freeTitle.trim()}>
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Création...
                </>
              ) : (
                "Créer la démarche"
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
